import React from 'react'
import {MDXProvider} from '@mdx-js/react'
import dayjs from 'dayjs'
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  TelegramShareButton,
  TelegramIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from 'react-share'
import {Hero} from './blog-list-page'
import {Typography} from '../components/Typography'
import {Container} from '../components/Container'
import {AuthorCard} from '../components/AuthorCard'
import imgDesktop from '../img/blog-desk.png'
import imgMobile from '../img/blog-mobile.png'

const Breadcrumbs = ({title}) => {
  return (
    <div className="py-8">
      <Typography as="a" uppercase>
        Blog
      </Typography>{' '}
      <Typography as="span">/ </Typography>
      <Typography as="a" uppercase>
        {title}
      </Typography>
    </div>
  )
}

const components = {
  h1: ({children}) => (
    <Typography variant="heading" size="lg">
      {children}
    </Typography>
  ),
  h2: ({children}) => (
    <Typography variant="heading" size="lg">
      {children}
    </Typography>
  ),
  h3: ({children}) => (
    <Typography variant="heading" size="lg">
      {children}
    </Typography>
  ),
  h4: ({children}) => <Typography variant="heading">{children}</Typography>,
  h5: ({children}) => <Typography variant="heading">{children}</Typography>,
  h6: ({children}) => <Typography variant="heading">{children}</Typography>,
  p: ({children}) => <Typography className="my-4">{children}</Typography>,
  strong: ({children}) => <strong className="font-bold">{children}</strong>,
  em: ({children}) => <em>{children}</em>,
  a: ({children}) => (
    <a className="text-blue-500" href={children}>
      {children}
    </a>
  ),
  img: ({src, alt, title, ...props}) => {
    let status = 'img'
    let element = <img src={src} alt={alt} title={title} className="w-full" />

    const hasYoutube = /www\.youtube\.com/g
    const hasVimeo = /vimeo\.com/g

    status = hasYoutube.test(src) ? 'youtube' : status
    status = hasVimeo.test(src) ? 'vimeo' : status

    if (status === 'youtube') {
      const id = src.split('=')[1]
      element = (
        <iframe
          title={title}
          width="100%"
          height="350"
          src={`https://www.youtube.com/embed/${id}`}
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        ></iframe>
      )
    }

    if (status === 'vimeo') {
      const {videoId} =
        src.match(/https:\/\/vimeo.com\/(?<videoId>[0-9]+)\/.+/)?.groups ?? {}

      const videoLinkPlayer = `https://player.vimeo.com/video/${videoId}`

      const id = src.split('=')[1]
      element = (
        <iframe
          title={title}
          src={videoLinkPlayer}
          width="100%"
          height="350"
          frameBorder="0"
          allowFullScreen
          allow="autoplay; fullscreen"
        ></iframe>
      )
    }

    return element
  },
}

export const BlogPageTemplate = ({
  children,
  title,
  author,
  publish,
  urlShare,
  ...props
}) => (
  <>
    <Hero imgDesktop={imgDesktop} imgMobile={imgMobile} />
    <Container>
      <div className="mx-auto max-w-2xl px-8 lg:px-0">
        <Breadcrumbs title={title} />
        <Typography variant="heading" color="blue-500" className="my-2">
          {title}
        </Typography>
        <Typography>
          <Typography as="span">{author}</Typography>
          <Typography as="span">|</Typography>
          <Typography as="span">
            {dayjs(publish).locale('pt-br').format('DD [de] MMMM [de] YYYY')}
          </Typography>
        </Typography>
        <div className="my-4 space-x-4">
          <EmailShareButton url={urlShare}>
            <EmailIcon size={32} round={true} />
          </EmailShareButton>
          <FacebookShareButton url={urlShare}>
            <FacebookIcon size={32} round={true} />
          </FacebookShareButton>
          <LinkedinShareButton url={urlShare}>
            <LinkedinIcon size={32} round={true} />
          </LinkedinShareButton>
          <TelegramShareButton url={urlShare}>
            <TelegramIcon size={32} round={true} />
          </TelegramShareButton>
          <TwitterShareButton url={urlShare}>
            <TwitterIcon size={32} round={true} />
          </TwitterShareButton>
          <WhatsappShareButton url={urlShare}>
            <WhatsappIcon size={32} round={true} />
          </WhatsappShareButton>
        </div>
        <div className="my-8 max-w-2xl">
          <MDXProvider components={components}>{children}</MDXProvider>
        </div>
        <AuthorCard authorName={author} />
      </div>
    </Container>
  </>
)

const Template = ({children, pageContext, location}) => {
  return (
    <BlogPageTemplate {...pageContext?.frontmatter} urlShare={location.href}>
      {children}
    </BlogPageTemplate>
  )
}

export default Template
